import { FunctionComponent, h } from "preact"
import distinctSensor, { CORRECTED } from "../../utils/distinctSensor"
import useRaw, { PayloadPercentage } from "../../hooks/useRaw"

import { BsFileEarmarkArrowDown } from "react-icons/bs"
import { Button } from "../../parts/buttons"
import Container from "../../parts/container"
import Pagination from "../../components/pagination"
import { SensorTypeQuery } from "../../enum/sensor.enum"
import TopBar from "../../components/topbar"
import TopFilter from "../../components/TopFilter"
import moment from "moment"
import { rawToExcel } from "./toexcel"
import useCems from "../../hooks/useCems"
import useMills from "../../hooks/useMills"
import useObjectState from "../../hooks/useObjectState"
import usePagination from "../../hooks/usePagination"
import useTopFilter from "../../hooks/useTopFilter"
import { usePromiseTracker } from "react-promise-tracker"
import Loader from 'react-loader-spinner'
import Renderif from "../../components/Renderif";
import Loading from "../../components/loading";

type RecordRawPageProps = h.JSX.HTMLAttributes<HTMLDivElement>;

const RecordRawPage: FunctionComponent<RecordRawPageProps> = () => {
  const { promiseInProgress } = usePromiseTracker();

  const topFilter = useTopFilter(true)
  const [filter, setFilter] = useObjectState({
    mil: "",
    cems: "",
  })
  
  const { data: mills } = useMills()
  const { data: cems } = useCems()
  const { data , isLoading} = useRaw({ type: SensorTypeQuery.raw, filter: topFilter.output })
  // const [isLoaded, setIsLoaded] = useState(false);
  const columns = data ? distinctSensor(data) : []
  

  const tableData = data
    ?.map((comp) =>
      comp.mils
        .filter(({ uuid }) => !filter.mil || uuid == filter.mil)
        .map(({ cems,name }) =>
          cems
            .filter(({ uuid }) => !filter.cems || uuid == filter.cems)
            .map((cem) =>
              cem.payloads?.map((payload) => {
                const values = columns.map((col) => {
                  const data = payload.payloads.find(({ name }) => name == col.name) as PayloadPercentage
                  return [data?.value, data?.value_condition_override] as [number, number]
                })
                const params = columns.map(
                  ({ name }) => cem.parameters.find(({ name: id }) => id == name)?.upper_threshold
                )
                return {
                  mils:name,
                  cemsId: cem.uuid,
                  cems: cem.name,
                  date: payload.measured_at,
                  values,
                  params,
                }
              })
            )
            .reduce((a, b) => (b ? a?.concat(b) : a), [])
        )
        .reduce((a, b) => (b ? a?.concat(b) : a), [])
    )
    .reduce((a, b) => (b ? a?.concat(b) : a), [])
  

  const pages = usePagination({ count: tableData?.length || 0, step: 20 })
  const showTable = tableData
    ?.slice(pages.output, pages.output + pages.step)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  return (
    <div className="min-vh-100">
      <TopBar title="SPARING" subtitle="Records Raw Data">
        <TopFilter hook={topFilter} />
      </TopBar>
      <Container>
        <div className="card">
          <div className="card-title">
            <h3>Raw Data</h3>
          </div>
          <div className="card-body">
            <div className="d-flex mb-3">
              <select
                value={filter.mil}
                onChange={({ currentTarget }) => setFilter({ mil: currentTarget.value, cems: "" })}
                className="me-2 w-auto form-select rounded-pill"
              >
                <option value="">Select Mills</option>
                {mills?.map((mil) => (
                  <option key={mil.uuid} value={mil.uuid}>
                    {mil.name}
                  </option>
                ))}
              </select>
              
              <Button
                className="ms-auto"
                icon={BsFileEarmarkArrowDown}
                onClick={() => {
                  if (tableData?.length) rawToExcel(columns, tableData)
                }}
                variant="primary"
              >
                Export
              </Button>
            </div>
            <div className="overflow-auto">
              <table className="table table-striped table-data table-center">
                <thead>
                  <tr>
                    <th>Mills</th>
                    <th>Date</th>
                    <th>Time</th>
                    {columns?.map((col) => (
                      <th key={col.name}>
                        {col.name} <br /> {col.uom}
                      </th>
                    ))}
                    {/* {columns
                      ?.filter(({ name }) => CORRECTED.includes(name))
                      .map((col) => (
                        <th key={col.name}>
                          {col.name} corrected <br /> {col.uom}
                        </th>
                      ))} */}
                  </tr>
                </thead>
                <tbody>
                  <Renderif isTrue={isLoading}>
                    <tr className="text-center">
                      <td className="text-center py-4" colSpan={2 * columns.length + 3}>
                        <Loading />
                      </td>
                    </tr>
                  </Renderif>
                  <Renderif isTrue={!isLoading}>
                  {showTable?.length ? (
                    showTable?.map((row, idx) => (
                      <tr key={"row-" + idx}>
                        <td className="text-nowrap">{row.mils}</td>
                        <td className="text-nowrap">{moment(row.date).format("YYYY-MM-DD")}</td>
                        <td>{moment(row.date).format("HH:mm")}</td>
                        {row.values.map((v, idx) => (
                          <td
                            key={"params-" + idx}
                            className={
                              row.params?.[idx] && Number(v[0]) > Number(row.params?.[idx]) ? "bg-error text-white" : ""
                            }
                          >
                            {v[0] && Number(v[0])?.toFixed(2)}
                          </td>
                        ))}
                        {/* {row.values
                          .map((v, idx) => (
                            <td
                              key={"params-" + idx}
                              className={
                                row.params?.[idx] && Number(v[1]) > Number(row.params?.[idx])
                                  ? "bg-error text-white"
                                  : ""
                              }
                            >
                              {v[1] && Number(v[1])?.toFixed(2)}
                            </td>
                          ))
                          .filter((_, idx) => {
                            return CORRECTED.includes(columns[idx].name)
                          })} */}
                      </tr>
                    ))
                  ) : (
                    <tr className="text-center">
                      <td className="text-center py-4" colSpan={2 * columns.length + 3}>
                        No data.
                      </td>
                    </tr>
                  )}
                  </Renderif>
                </tbody>
              </table>
            </div>
            <div className="my-2 d-flex justify-content-end">
              <Pagination hook={pages} />
            </div>
          </div>
        </div>
      </Container>
    </div>
  )
}

export default RecordRawPage
